import { useEffect, type ReactNode } from 'react';
import '@/components/merchandising/pdp-refinements.css';
import { CONVERSION_COPY } from './content';
import MagAbsorbStory from './MagAbsorbStory';
import IngredientScrollStory from './IngredientScrollStory';
import FormulaClosingStory from './FormulaClosingStory';
import { DecisionHero, FormulaSection, EvidenceSection, RoutineSection, OfferRecap, BuyerFaq, MobilePurchaseBar } from './ConversionSections';

export function ConversionProductPage({ handle, gallery, purchase, formula, evidence, routine, recap, faqs, stickyAction, priceLabel, supplyLabel, hideSticky = false, tldr, facts, compare, bundleOffer, narrative }: {
  handle: string; gallery: ReactNode; purchase: ReactNode; formula: ReactNode; evidence: ReactNode; routine: ReactNode; recap: ReactNode; faqs: { q: string; a: string }[]; stickyAction: ReactNode; priceLabel?: string; supplyLabel: string; hideSticky?: boolean;
  /** Optional restored sections; omit to render nothing. */
  tldr?: ReactNode; facts?: ReactNode; compare?: ReactNode; bundleOffer?: ReactNode;
  /** Reference-style concern explorer + formula panel; replaces the old lifestyle block. */
  narrative?: ReactNode;
}) {
  const copy = CONVERSION_COPY[handle];
  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const sync = () => document.body.classList.toggle('has-sticky-cta', media.matches && !hideSticky);
    sync(); media.addEventListener('change', sync);
    return () => { media.removeEventListener('change', sync); document.body.classList.remove('has-sticky-cta'); };
  }, [hideSticky]);
  if (!copy) return null;
  const isMag = handle === 'mag-absorb-pro';
  const chooseMagSupply = () => {
    const purchaseBox = document.getElementById('buy-box');
    if (!purchaseBox) return;
    purchaseBox.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };
  // Per-SKU accent: Liver Biome+ uses olive, Ova Balance uses its purple accent; Mag Absorb Pro stays brand blue.
  const isLiver = handle.startsWith('liver-biome');
  const isOva = handle === 'ova-balance';
  const storyProduct = isLiver ? 'liver' : isOva ? 'ova' : null;
  const chooseStorySupply = () => {
    const purchaseBox = document.getElementById('buy-box');
    if (!purchaseBox) return;
    purchaseBox.scrollIntoView({ block: 'center', behavior: 'instant' });
  };
  const skuStyle = isLiver
    ? ({
        // Match the narrative section's olive (#59663d family).
        '--twc-accent': '#59663d',
        '--twc-accent-deep': '#465131',
        '--twc-soft': '#f2f4e9',
        // Richer olive gradient fills: darker overall (button, bars, selected pack).
        '--twc-accent-fill': 'linear-gradient(135deg, #6f8040 0%, #4c5a2f 52%, #2e3a1e 100%)',
        '--twc-soft-fill': 'linear-gradient(135deg, #f5f7ec 0%, #e4e9d3 100%)',
        // Headline text gradient: deeper olive range.
        '--twc-headline-fill': 'linear-gradient(118deg, #7d8f4a 0%, #56632f 46%, #38411f 78%, #262e14 100%)',
      } as React.CSSProperties)
    : isOva
    ? ({
        // Match the narrative section's purple (#67458b family).
        '--twc-accent': '#67458b',
        '--twc-accent-deep': '#50346e',
        '--twc-soft': '#f4f0f9',
        // Richer purple gradient fills: darker overall (button, bars, selected pack).
        '--twc-accent-fill': 'linear-gradient(135deg, #8552bd 0%, #5b3b82 52%, #351f4f 100%)',
        '--twc-soft-fill': 'linear-gradient(135deg, #f6f1fc 0%, #e7dbf3 100%)',
        // Headline text gradient: deeper purple range.
        '--twc-headline-fill': 'linear-gradient(118deg, #9466c9 0%, #6a4596 46%, #4c3070 78%, #35204f 100%)',
      } as React.CSSProperties)
    : {
        // Mag Absorb Pro: brand blue family, same enriched gradient treatment as the other SKUs.
        '--twc-accent': '#1E5EFF',
        '--twc-accent-deep': '#123db8',
        '--twc-soft': '#eef3ff',
        // Richer blue gradient fills: darker overall (button, bars, selected pack).
        '--twc-accent-fill': 'linear-gradient(135deg, #3a6ef0 0%, #154ccf 52%, #0c2f96 100%)',
        '--twc-soft-fill': 'linear-gradient(135deg, #eff4ff 0%, #d9e4fb 100%)',
        // Headline text gradient: deeper blue range.
        '--twc-headline-fill': 'linear-gradient(118deg, #5277e6 0%, #2250d6 46%, #143191 78%, #0b1c49 100%)',
      } as React.CSSProperties;
  return <div className={`truwe-conversion twc-pdp-shell twc-headline-accent`} data-product={handle} style={skuStyle}>
    <DecisionHero copy={copy} gallery={gallery} purchase={purchase} />
    {bundleOffer ? <div className="twc-section twc-bundle-offer" data-engagement-section="fbt">{bundleOffer}</div> : null}
    {tldr}
    <div id="twc-for-you" data-engagement-section="tldr">{narrative}</div>
    {(isMag || storyProduct) && <EvidenceSection>{evidence}</EvidenceSection>}
    {isMag ? (
      <FormulaSection copy={copy} bare>
        <MagAbsorbStory part="formula" headingClassName="" onChooseSupply={chooseMagSupply} />
      </FormulaSection>
    ) : storyProduct ? (
      <FormulaSection copy={copy} bare>
        <IngredientScrollStory key={storyProduct} product={storyProduct} />
        {formula}
      </FormulaSection>
    ) : formula ? <FormulaSection copy={copy} bare>{formula}</FormulaSection> : null}
    {facts}
    {!isMag && !storyProduct && <EvidenceSection>{evidence}</EvidenceSection>}
    {compare}
    <RoutineSection copy={copy}>{routine}</RoutineSection>
    {isMag ? (
      <div id="twc-offer" data-engagement-section="comparison">
        <MagAbsorbStory part="closing" headingClassName="" onChooseSupply={chooseMagSupply} offerDetails={recap} />
      </div>
    ) : storyProduct ? (
      <div id="twc-offer" data-engagement-section="comparison">
        <FormulaClosingStory product={storyProduct} onChooseSupply={chooseStorySupply} offerDetails={recap} />
      </div>
    ) : <OfferRecap>{recap}</OfferRecap>}
    <BuyerFaq items={faqs} />
    <MobilePurchaseBar priceLabel={priceLabel} supplyLabel={supplyLabel} action={stickyAction} hidden={hideSticky} />
  </div>;
}
